"use client";
import React, { useEffect, useState, useRef } from "react";

import {
  Checkbox,
  FormControl,
  SelectChangeEvent,
  InputLabel,
  MenuItem,
  Select,
  VeTypography,
  ListItemText,
  VeBox,
  TextField,
  InputAdornment,
  IconButton,
  FontAwesomeIcon,
  Divider,
} from "../../mui";
import { limiter } from "../../hoc";
import { useController } from "react-hook-form";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { VeTooltip } from "../VeTooltip/VeTooltip";

interface OptionType {
  label: string;
  value: string | number;
  id?: number;
  key?: number;
  disabled?: boolean;
  tag?: string;
  warning?: boolean;
  dividerAboveOption?: boolean;
}

interface SelectProps {
  label?: string;
  error?: boolean;
  errorText?: string;
  style?: React.CSSProperties;
  register?: any;
  options: OptionType[];
  disabled?: boolean;
  name: string;
  helperText?: string | JSX.Element | boolean;
  placeholder?: string;
  value?: string | string[] | number[];
  multiple?: boolean;
  defaultValue?: string | number;
  selected?: any;
  required?: boolean;
  onChangeData?: any;
  limiterLimit?: any;
  limiterLimitMultiple?: any;
  control?: any;
  disabledValues?: (string | number)[];
  dropdownWidth?: string;
  showSelectedLimit?: number;
  fontSize?: string;
  setDefault?: boolean;
  dropdownSearch?: boolean;
  checkIcon?: boolean;
  colorCube?: boolean; // Renders a small square with bg color of its value. Mostly used for color picking using VeSelect
  onChangeCallBack?: any;
}

export const DropDownIcon = (props: React.SVGProps<SVGSVGElement>) => {
  return (
    <svg
      style={{ marginRight: "10px", marginTop: "-1px" }}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M9.411 13.0896C9.73642 13.4151 10.2649 13.4151 10.5903 13.0896L15.5886 8.09132C15.914 7.76591 15.914 7.23744 15.5886 6.91203C15.2632 6.58662 14.7347 6.58662 14.4093 6.91203L9.99935 11.322L5.58937 6.91463C5.26396 6.58922 4.73549 6.58922 4.41007 6.91463C4.08466 7.24004 4.08466 7.76851 4.41007 8.09392L9.4084 13.0923L9.411 13.0896Z"
        fill="#5F6368"
      />
    </svg>
  );
};

export function VeSelect({
  label,
  error,
  errorText,
  disabled,
  register,
  options,
  name,
  placeholder,
  value,
  defaultValue,
  multiple,
  required,
  onChangeData,
  selected,
  helperText,
  limiterLimitMultiple = 40,
  limiterLimit = 50,
  showSelectedLimit = limiterLimit,
  disabledValues = [], // Default to an empty array
  style,
  dropdownWidth,
  control,
  fontSize = "1rem",
  setDefault = false,
  dropdownSearch = false,
  checkIcon = false,
  colorCube = false,
  onChangeCallBack,
}: SelectProps) {
  const {
    field: { value: controlValue, onChange: controlOnChange },
  } = control
    ? useController({
        name,
        control,
      })
    : { field: { value: "", onChange: () => {} } };
  const [selectedValues, setSelectedValues] = useState<string[] | number[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filteredOptions, setFilteredOptions] = useState<OptionType[]>(options);

  // Add a ref for the search input
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (multiple && Array.isArray(value)) {
      // For multiselect, always sync with value prop (including empty arrays)
      setSelectedValues([...value] as any[]);
    } else if (multiple && !value) {
      // If value is undefined/null for multiselect, set to empty array
      setSelectedValues([]);
    }
    // For single select, we don't use selectedValues state - value prop is used directly
  }, [value, multiple]);

  // Update filtered options when original options change
  useEffect(() => {
    if (searchTerm) {
      // If there's a search term, apply filter
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredOptions(filtered);
    } else {
      // If no search term, show all options
      setFilteredOptions(options);
    }
  }, [options]);

  useEffect(() => {
    if (!setDefault && control) {
      if (controlValue?.length === 0) {
        setSelectedValues([]);
      } else {
        setSelectedValues(multiple ? controlValue : []);
      }
    }
  }, [controlValue]);

  // Add effect to focus input when dropdown opens
  // useEffect(() => {
  //   if (isOpen && dropdownSearch && searchInputRef.current) {
  //     // Use a small delay to ensure the dropdown is fully rendered
  //     const timer = setTimeout(() => {
  //       if (searchInputRef.current) {
  //         searchInputRef.current.focus();
  //       }
  //     }, 50);

  //     return () => clearTimeout(timer);
  //   }
  // }, [isOpen, dropdownSearch]);

  const CustomDropDownIcon = ({
    disabled,
    ...props
  }: React.SVGProps<SVGSVGElement> & { disabled?: boolean }) => {
    return (
      <svg
        style={{ marginRight: "10px", marginTop: "-1px" }}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
      >
        <path
          d="M9.411 13.0896C9.73642 13.4151 10.2649 13.4151 10.5903 13.0896L15.5886 8.09132C15.914 7.76591 15.914 7.23744 15.5886 6.91203C15.2632 6.58662 14.7347 6.58662 14.4093 6.91203L9.99935 11.322L5.58937 6.91463C5.26396 6.58922 4.73549 6.58922 4.41007 6.91463C4.08466 7.24004 4.08466 7.76851 4.41007 8.09392L9.4084 13.0923L9.411 13.0896Z"
          fill={disabled ? "#8F9295" : "#3D465A"}
        />
      </svg>
    );
  };

  const handleChange = (event: SelectChangeEvent<typeof selectedValues>) => {
    const {
      target: { value },
    } = event;
    if (control) controlOnChange(value);
    setSelectedValues(typeof value === "string" ? value.split(",") : value);
    if (selected) {
      selected(typeof value === "string" ? value.split(",") : value);
    }
    if (!multiple) {
      setSearchTerm("");
    }
    if (onChangeCallBack) onChangeCallBack();
  };

  const handleChangeMultiple = (
    event: SelectChangeEvent<typeof selectedValues>,
  ) => {
    const {
      target: { value },
    } = event;
    if (control)
      controlOnChange(typeof value === "string" ? value.split(",") : value);
    if (onChangeData)
      onChangeData(typeof value === "string" ? value.split(",") : value);
    setSelectedValues(typeof value === "string" ? value.split(",") : value);
    if (onChangeCallBack) onChangeCallBack();
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term: string = event.target.value;
    setSearchTerm(term);

    // Directly filter options here instead of in useEffect
    if (term) {
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(term.toLowerCase()),
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }

    // Prevent event bubbling
    event.stopPropagation();
  };

  const handleClearSearch = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSearchTerm("");
    setFilteredOptions(options);
    // Re-focus the input after clearing
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    // Reset to initial options when opening
    setFilteredOptions(options);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchTerm("");
    setFilteredOptions(options);
  };

  const isDisabled = (value: string | number) => disabledValues.includes(value);

  const renderSelectValue = (selected: string[] | string) => {
    if (multiple && Array.isArray(selected)) {
      return limiter({
        strings: selected
          .map(
            (value) =>
              options?.find((option) => option.value === value)?.label || "",
          )
          .join(", "),
        limit: limiterLimitMultiple,
      });
    } else {
      const selectedItem = options?.find((option) => option.value === selected);
      return selectedItem
        ? limiter({
            strings: selectedItem.label,
            limit: showSelectedLimit,
          })
        : "";
    }
  };

  // Handler to prevent menu from closing when clicking in the search box
  const handleSearchBoxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const fontColor = disabled ? "#8F9295" : "#3D465A";

  return (
    <div style={{ color: fontColor, width: "100%" }}>
      <FormControl fullWidth error={error}>
        <InputLabel id={`select-label-${name}`}>
          {required ? (
            <VeBox
              sx={{
                color: disabled ? "#8F9295 !important" : "#3D465A !important",
              }}
              display={"flex"}
            >
              {label}
              <VeBox marginLeft={"2px"} color={"red"}>
                *
              </VeBox>
            </VeBox>
          ) : (
            <VeBox
              sx={{
                color: disabled ? "#8F9295 !important" : "#3D465A !important",
              }}
            >
              {label}
            </VeBox>
          )}
        </InputLabel>

        <Select
          multiple={multiple}
          labelId={`select-label-${name}`}
          id={`select-${name}`}
          disabled={disabled}
          name={name}
          {...(register && register(name))}
          value={
            multiple
              ? control
                ? controlValue ?? []
                : Array.isArray(value)
                  ? value
                  : selectedValues
              : control
                ? controlValue ?? ""
                : value ?? selectedValues
          }
          label={label}
          displayEmpty={!!placeholder}
          renderValue={(selected) => {
            if (multiple) {
              if (
                Array.isArray(selected) &&
                selected.length === 0 &&
                placeholder
              ) {
                return <span style={{ color: "#8F9295" }}>{placeholder}</span>;
              }
              return renderSelectValue(selected as string[]);
            } else {
              if (
                (selected === "" ||
                  selected === null ||
                  selected === undefined) &&
                placeholder
              ) {
                return <span style={{ color: "#8F9295" }}>{placeholder}</span>;
              }
              return renderSelectValue(selected as string);
            }
          }}
          onOpen={handleOpen}
          onClose={handleClose}
          open={dropdownSearch ? isOpen : undefined}
          IconComponent={(props) => (
            <CustomDropDownIcon disabled={disabled} {...props} />
          )}
          MenuProps={{
            PaperProps: {
              style: {
                width: dropdownWidth ? dropdownWidth : undefined,
                maxHeight: 350,
              },
              sx: {
                "& .MuiList-root": {
                  paddingTop: dropdownSearch ? "0px" : "8px",
                  padding: "4px",
                },
              },
            },
            // This is critical - don't auto-focus the first menuitem
            autoFocus: false,
            // Don't close the menu when clicking inside the search box
            disableAutoFocusItem: true,
          }}
          sx={{
            lineHeight: "2rem",
            overflow: "hidden",
            ...style,
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px !important",
            },
          }}
          defaultValue={defaultValue}
          onChange={
            multiple
              ? handleChangeMultiple
              : onChangeData
                ? onChangeData
                : handleChange
          }
        >
          {dropdownSearch && (
            <MenuItem
              disableRipple
              disableTouchRipple
              disabled={false}
              style={{
                position: "sticky",
                top: 0,
                backgroundColor: "#fff",
                zIndex: 1,
                width: "100%",
                padding: "5px",
                border: "none",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                cursor: "default",
              }}
              onClick={handleSearchBoxClick}
            >
              <TextField
                fullWidth
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search..."
                variant="outlined"
                size="small"
                autoFocus
                inputRef={searchInputRef}
                onClick={handleSearchBoxClick}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.stopPropagation();
                    handleClose();
                  }
                  // Prevent event propagation for all key events
                  e.stopPropagation();
                }}
                onFocus={(e) => e.stopPropagation()}
                InputProps={{
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        tabIndex={-1}
                      >
                        <span>×</span>
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </MenuItem>
          )}

          {filteredOptions?.length === 0 && (
            <MenuItem disabled>
              {" "}
              <VeTypography variant="label3Secondary">
                {placeholder || "No options found"}
              </VeTypography>
            </MenuItem>
          )}

          {filteredOptions?.map((item) =>
            multiple ? (
              <MenuItem
                value={item?.value}
                key={item?.key || item?.id || item?.value}
                disabled={isDisabled(item?.value) || item?.disabled}
                sx={{ whiteSpace: "normal !important", fontSize: fontSize }}
              >
                <Checkbox
                  checked={
                    (control ? controlValue || [] : selectedValues)?.indexOf(
                      item?.value as never,
                    ) > -1
                  }
                  sx={{ color: "#d5dbe1" }}
                />
                {item.label.length > limiterLimitMultiple ? (
                  <VeTooltip title={item.label} placement="top" arrow>
                    <span>
                      <VeTypography variant="label3Secondary">
                        {limiter({
                          strings: item.label,
                          limit: limiterLimitMultiple,
                        })}
                      </VeTypography>
                    </span>
                  </VeTooltip>
                ) : (
                  <VeTypography variant="label3Secondary">
                    {limiter({
                      strings: item.label,
                      limit: limiterLimitMultiple,
                    })}
                  </VeTypography>
                )}
                {item?.tag && (
                  <VeBox
                    display="flex"
                    alignItems="center"
                    gap="4px"
                    padding="3px 10px"
                    borderRadius="50px"
                    sx={{ backgroundColor: "#F3F3F3", marginLeft: "auto" }}
                  >
                    <VeTypography variant="label4Secondary">
                      {item?.tag}
                    </VeTypography>
                  </VeBox>
                )}
              </MenuItem>
            ) : (
              <MenuItem
                value={item?.value}
                key={item?.key || item?.id || item?.value}
                disabled={isDisabled(item?.value) || item?.disabled}
                sx={{
                  whiteSpace: "normal !important",
                  fontSize: fontSize,
                  minHeight: "35px",
                  borderTop: item?.dividerAboveOption
                    ? "1px solid #E8E8E8"
                    : "none",
                }}
              >
                <VeBox
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  width={"100%"}
                >
                  {item?.label?.length > limiterLimit ? (
                    <VeTooltip title={item.label} placement="top" arrow>
                      <span>
                        <VeTypography
                          variant={
                            item?.warning ? "label2Error" : "label2Secondary"
                          }
                        >
                          {limiter({
                            strings: item.label,
                            limit: limiterLimit,
                          })}
                        </VeTypography>
                      </span>
                    </VeTooltip>
                  ) : (
                    <VeTypography
                      variant={
                        item?.warning ? "label2Error" : "label2Secondary"
                      }
                      display={"flex"}
                      alignItems={"center"}
                      gap={"12px"}
                    >
                      {/* THIS IS USED TO DISPLAY A COLOR NEXT TO AN OPTION , mostly used for picking colours */}
                      {colorCube && (
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            backgroundColor: (item?.value as string) || "white",
                          }}
                        />
                      )}
                      {limiter({
                        strings: item.label,
                        limit: limiterLimit,
                      })}
                    </VeTypography>
                  )}

                  {checkIcon && item?.value === value && (
                    <FontAwesomeIcon
                      icon={faCheck}
                      color="#5F6368"
                      height={"6.67"}
                      width="9.33"
                    />
                  )}
                </VeBox>
                {item?.tag && (
                  <VeBox
                    display="flex"
                    alignItems="center"
                    gap="4px"
                    padding="3px 10px"
                    borderRadius="50px"
                    sx={{ backgroundColor: "#F3F3F3", marginLeft: "auto" }}
                  >
                    <VeTypography variant="label4Secondary">
                      {item?.tag}
                    </VeTypography>
                  </VeBox>
                )}
              </MenuItem>
            ),
          )}
        </Select>
        {error && (
          <VeTypography
            style={{ marginLeft: "10px", marginTop: "8px" }}
            variant="label4ErrorPrimary"
          >
            {errorText}
          </VeTypography>
        )}
        {helperText && (
          <VeTypography
            style={{ marginLeft: "10px", marginTop: "5px" }}
            variant="label4Disabled"
          >
            {helperText}
          </VeTypography>
        )}
      </FormControl>
    </div>
  );
}
